import React, { useCallback, useMemo } from 'react';
import { Joyride, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useI18n } from '../i18n';

const getSteps = (t) => [
    {
        target: 'body',
        placement: 'center',
        title: t('tutWelcomeTitle'),
        content: <p>{t('tutWelcomeContent')}</p>,
    },
    {
        target: '[data-tour="avatar-labels"]',
        placement: 'right',
        title: t('tutContextTitle'),
        content: (
            <div className="space-y-2">
                <p>{t('tutContextContent1')}</p>
                <p>{t('tutContextContent2')}</p>
                <p>{t('tutContextContent3')}</p>
            </div>
        ),
    },
    {
        target: '[data-tour="mic"]',
        placement: 'top',
        title: t('tutMicTitle'),
        content: <p>{t('tutMicContent')}</p>,
    },
    {
        target: '[data-tour="stop-audio"]',
        placement: 'top',
        title: t('tutStopTitle'),
        content: <p>{t('tutStopContent')}</p>,
    },
    {
        target: '[data-tour="text-input"]',
        placement: 'top',
        title: t('tutTextTitle'),
        content: <p>{t('tutTextContent')}</p>,
    },
    {
        target: '[data-tour="chat-toggle"]',
        placement: 'top',
        title: t('tutChatTitle'),
        content: <p>{t('tutChatContent')}</p>,
    },
    {
        target: '[data-tour="sidebar-scenario"]',
        placement: 'left',
        title: t('tutScenarioTitle'),
        content: <p>{t('tutScenarioContent')}</p>,
    },
    {
        target: '[data-tour="sidebar-timeline"]',
        placement: 'left',
        title: t('tutTimelineTitle'),
        content: (
            <div className="space-y-2">
                <p>{t('tutTimelineContent1')}</p>
                <p>{t('tutTimelineContent2')}</p>
            </div>
        ),
    },
    {
        target: '[data-tour="sidebar-progress"]',
        placement: 'left',
        title: t('tutProgressTitle'),
        content: <p>{t('tutProgressContent')}</p>,
    },
    {
        target: '[data-tour="sidebar-hint"]',
        placement: 'left',
        title: t('tutHintsTitle'),
        content: <p>{t('tutHintsContent')}</p>,
    },
    {
        target: '[data-tour="sidebar-skills"]',
        placement: 'left',
        title: t('tutSkillsTitle'),
        content: <p>{t('tutSkillsContent')}</p>,
    },
    {
        target: '[data-tour="header-help"]',
        placement: 'bottom',
        title: t('tutHelpTitle'),
        content: <p>{t('tutHelpContent')}</p>,
    },
];

const JOYRIDE_OPTIONS = {
    backgroundColor: '#FFFFFF',
    arrowColor: '#FFFFFF',
    primaryColor: '#1A4731',
    textColor: '#1c1917',
    overlayColor: 'rgba(28, 25, 23, 0.45)',
    zIndex: 10000,
    skipBeacon: true,
    showProgress: true,
    overlayClickAction: false,
    buttons: ['back', 'skip', 'primary'],
    spotlightPadding: 8,
    width: 340,
};

const JOYRIDE_STYLES = {
    tooltip: {
        borderRadius: '16px',
        border: '1px solid #E7E5E0',
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        padding: '20px 24px',
        background: '#FFFFFF',
    },
    tooltipTitle: {
        color: '#1c1917',
        fontSize: '14px',
        fontWeight: '700',
        marginBottom: '8px',
        letterSpacing: '-0.01em',
    },
    tooltipContent: {
        fontSize: '13px',
        lineHeight: '1.65',
        color: '#78716c',
        padding: 0,
    },
    tooltipFooter: {
        marginTop: '16px',
        borderTop: '1px solid #F5F3EF',
        paddingTop: '14px',
    },
    buttonNext: {
        background: '#1A4731',
        borderRadius: '10px',
        fontSize: '12px',
        padding: '8px 18px',
        color: '#fff',
        fontWeight: '600',
        letterSpacing: '0.01em',
        boxShadow: 'none',
    },
    buttonBack: {
        color: '#a8a29e',
        fontSize: '12px',
        marginRight: '8px',
        fontWeight: '500',
    },
    buttonSkip: {
        color: '#a8a29e',
        fontSize: '12px',
        fontWeight: '500',
    },
    spotlight: {
        borderRadius: '14px',
    },
};

const getLocale = (t) => ({
    back: t('jrBack'),
    close: t('jrClose'),
    last: t('jrLast'),
    next: t('jrNext'),
    nextWithProgress: t('jrNextLabeled'),
    open: t('jrOpen'),
    skip: t('jrSkip'),
});

const ChatTutorial = ({ run, stepIndex, onStepChange }) => {
    const { t } = useI18n();
    const steps = useMemo(() => getSteps(t), [t]);
    const locale = useMemo(() => getLocale(t), [t]);

    const handleJoyrideEvent = useCallback(
        (data) => {
            const { action, index, type, status } = data;
            onStepChange({ stepIndex: index, action, type, status });
        },
        [onStepChange]
    );

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous
            scrollToFirstStep
            options={JOYRIDE_OPTIONS}
            styles={JOYRIDE_STYLES}
            locale={locale}
            onEvent={handleJoyrideEvent}
        />
    );
};

export default ChatTutorial;
